import { FormEvent, useEffect, useRef, useState } from 'react';
import { api } from '../lib/api';
import SimpleFolderTree, { SimpleFolderRow } from '../components/SimpleFolderTree';

type Resource = {
  id: number;
  name: string;
  description: string | null;
  file_key: string | null;
  category: string | null;
  folder_id: number | null;
  uploaded_at: string;
  uploaded_by_name: string | null;
};

export default function Resources() {
  const [items, setItems] = useState<Resource[]>([]);
  const [folders, setFolders] = useState<SimpleFolderRow[]>([]);
  const [name, setName] = useState('');
  const [category, setCategory] = useState('');
  const [description, setDescription] = useState('');
  const [folderId, setFolderId] = useState<string>('');
  const [busy, setBusy] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  async function refresh() {
    const [r, f] = await Promise.all([
      api.get<{ resources: Resource[] }>('/resources'),
      api.get<{ folders: SimpleFolderRow[] }>('/resource-folders'),
    ]);
    setItems(r.resources);
    setFolders(f.folders);
  }
  useEffect(() => {
    refresh();
  }, []);

  async function onUpload(e: FormEvent) {
    e.preventDefault();
    const f = fileRef.current?.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', f);
      fd.append('name', name || f.name);
      if (description) fd.append('description', description);
      if (category) fd.append('category', category);
      if (folderId) fd.append('folder_id', folderId);
      const res = await fetch('/api/v1/resources', {
        method: 'POST',
        credentials: 'include',
        body: fd,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: 'Upload impossible' }));
        alert((err as { error?: string }).error ?? 'Upload impossible');
      } else {
        setName('');
        setDescription('');
        setCategory('');
        setFolderId('');
        if (fileRef.current) fileRef.current.value = '';
        refresh();
      }
    } finally {
      setBusy(false);
    }
  }

  async function addFolder(name: string, parentId: number | null) {
    await api.post('/resource-folders', { name, parent_id: parentId });
    refresh();
  }
  async function renameFolder(id: number, name: string) {
    await api.patch(`/resource-folders/${id}`, { name });
    refresh();
  }
  async function removeFolder(id: number) {
    await api.del(`/resource-folders/${id}`);
    refresh();
  }
  async function moveResource(resId: number, folderId: number | null) {
    await api.patch(`/resources/${resId}/move`, { folder_id: folderId });
    refresh();
  }
  async function delResource(id: number) {
    if (!confirm('Supprimer cette ressource ?')) return;
    await api.del(`/resources/${id}`);
    refresh();
  }

  // Construire le chemin de chaque dossier pour le select
  const folderPaths = (() => {
    const byId = new Map(folders.map((f) => [f.id, f]));
    function pathOf(id: number): string {
      const segs: string[] = [];
      let cur: number | null = id;
      for (let i = 0; i < 16 && cur !== null; i++) {
        const row = byId.get(cur);
        if (!row) break;
        segs.unshift(row.name);
        cur = row.parent_id;
      }
      return segs.join(' / ');
    }
    return folders
      .map((f) => ({ id: f.id, path: pathOf(f.id) }))
      .sort((a, b) => a.path.localeCompare(b.path));
  })();

  return (
    <>
      <div className="topbar">
        <h1>Ressources</h1>
        <span className="card-meta">{items.length} ressources</span>
      </div>

      <form className="card" onSubmit={onUpload} style={{ marginBottom: 16 }}>
        <div className="card-meta" style={{ marginBottom: 10 }}>
          Chartes BIM, templates, normes SIA / ISO 19650, contacts, documents de référence stables.
          Organise-les dans des dossiers.
        </div>
        <div className="field">
          <label>Fichier</label>
          <input ref={fileRef} type="file" required />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div className="field">
            <label>Nom affiché (facultatif)</label>
            <input value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="field">
            <label>Dossier</label>
            <select value={folderId} onChange={(e) => setFolderId(e.target.value)}>
              <option value="">— Racine (non classé)</option>
              {folderPaths.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.path}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div className="field">
            <label>Catégorie (tag libre)</label>
            <input
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              placeholder="ex. Charte BIM, Normes, Contacts…"
            />
          </div>
          <div className="field">
            <label>Description</label>
            <input value={description} onChange={(e) => setDescription(e.target.value)} />
          </div>
        </div>
        <button className="btn btn-primary" disabled={busy}>
          {busy ? 'Upload…' : 'Téléverser'}
        </button>
      </form>

      <SimpleFolderTree
        folders={folders}
        items={items}
        headerLabel="📚 Ressources classées"
        rootLabel="Ressources non classées"
        onAddFolder={addFolder}
        onRenameFolder={renameFolder}
        onDeleteFolder={removeFolder}
        onMoveItem={moveResource}
        renderItem={(r) => (
          <div className="res-row">
            <span>📄</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 600 }}>{r.name}</div>
              {(r.description || r.category) && (
                <div className="card-meta" style={{ fontSize: 11 }}>
                  {r.category && <>{r.category} · </>}
                  {r.description}
                </div>
              )}
              <div className="card-meta" style={{ fontSize: 11 }}>
                {r.uploaded_by_name ?? '—'} · {new Date(r.uploaded_at).toLocaleDateString('fr-FR')}
              </div>
            </div>
            {r.file_key && (
              <a
                className="btn btn-voir"
                style={{ padding: '4px 10px', fontSize: 12 }}
                href={`/api/v1/files?key=${encodeURIComponent(r.file_key)}`}
                target="_blank"
                rel="noreferrer"
                onClick={(e) => e.stopPropagation()}
              >
                Ouvrir
              </a>
            )}
            <button
              onClick={(e) => {
                e.stopPropagation();
                delResource(r.id);
              }}
              className="note-del"
              title="Supprimer"
            >
              ×
            </button>
          </div>
        )}
      />
    </>
  );
}
